import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Grid,
    Box,
    Paper,
    Divider,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { getDateLocale } from '../../../lib/dateLocale';
import GetAppIcon from '@mui/icons-material/GetApp';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoneIcon from '@mui/icons-material/Done';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import PublishIcon from '@mui/icons-material/Publish';
import { useTranslation } from 'react-i18next';
import { getPath } from '../../Pages';
import RemittenceLineDialog from './RemittenceLineDialog';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import {
    subsetObject,
    updateElementInArray,
    formatPriceByLocale,
    getErrorMessage,
} from '../../../lib/utils';
import {
    AppContext,
    SttLabelForm,
    SttFormText,
    SttFormSelect,
    SttFormDate,
    SttTable,
    SttButtonSave,
    SttButtonDelete,
    SttTopControls,
    SttButton,
    UploadFileDialog,
    SttFullDialog,
} from '../../../sporttia/all';
import SttMshipDialog from '../../../sporttia/dialogs/SttMshipDialog';
import { useInteractionsFiles } from '../../../lib/hooks';
import { usePermission } from '../../../lib/usePermission';
import { PERMISSIONS } from '../../../lib/permissions';

type RemittenceLine = {
    id: number;
    name: string;
    description: string;
    iban: string;
    rejected?: string;
    price: number;
    errors?: string[];
    payment: {
        id: number;
        user?: {
            mship: {
                id: number;
            };
        };
    };
    priceShape?: {
        currency: string;
        locale: string;
    };
};

type LinesResponse = {
    rows: RemittenceLine[];
    count: number;
    page: number;
    pages: number;
};

type RemittenceType = {
    id: number;
    name: string;
    status: string;
    issueDate: string;
    created: string;
    trash?: boolean;
};

type Filters = {
    name: string;
    status: string | null;
};

export default function Remittence(): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const { can } = usePermission();
    // Devolver recibos cambia la remesa: `remittance.modify`
    const canRefund = can(PERMISSIONS.REMITTANCE.MODIFY);
    const { id } = useParams<{ id: string }>();
    const { downloadFile } = useInteractionsFiles();
    const navigate = useNavigate();
    const [creating, setCreating] = useState<boolean>(false);
    const [remittence, setRemittence] = useState<RemittenceType>();
    const [lines, setLines] = useState<LinesResponse>();
    const [idPaymentModal, setIdPaymentModal] = useState<number | null>(null);
    const [filters, setFilters] = useState<Filters>({
        name: '',
        status: null, // ERROR, RETURNED
    });
    const [openRemittenceLine, setOpenRemittenceLine] =
        useState<RemittenceLine | null>(null);
    const [openUploadFile, setOpenUploadFile] = useState<boolean>(false);
    const [mshipChosen, setMshipChosen] = useState<number | null>(null);
    const [selectRows, setSelectRows] = useState<RemittenceLine[]>([]);
    const [fillBics, setFillBics] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const loadLines = (p = { rows: 20, page: 1 }): void => {
        cxt?.api('GET', `/remittences/${id}/lines`, {
            params: {
                ...{
                    ...filters,
                    name: filters?.name ? filters?.name : undefined,
                },
                page: p.page,
                rows: p.rows,
            },
            success: (r: LinesResponse) => {
                setLines(r);
            },
        });
    };

    const loadRemittence = (): void => {
        cxt?.api('GET', `/remittences/${id}`, {
            params: {
                trash: true,
            },
            success: (r: { remittence: RemittenceType }) => {
                setRemittence(r.remittence);
                loadLines();

                setCreating(false);
            },
        });
    };

    const downloadCSVLines = (): void => {
        const params: Record<string, number[]> = {};

        if (selectRows.length > 0) {
            const newLines: number[] = [];

            selectRows.forEach((row: any) => newLines.push(row.id));

            params.idLines = newLines;
        }

        downloadFile(
            `/remittences/${remittence?.id}.csv`,
            'text/csv',
            params,
            'remesa.csv',
        );
    };

    const saveRemittence = (): void => {
        const params = subsetObject(remittence!, [
            'name',
            'status',
            'issueDate',
        ]);

        setLoading(true);
        // Create
        if (creating) {
            cxt?.api('POST', `/scs/${cxt?.sc?.id}/remittences`, {
                params,
                success: (r: { remittence: RemittenceType }) => {
                    cxt?.showMessage('S', t('generic.created'));
                    navigate(`/remittences/${r.remittence.id}`);
                    setLoading(false);
                },
                error: (err: any) => {
                    cxt.showMessage('E', getErrorMessage(err));
                    setLoading(false);
                },
            });

            // Update
        } else {
            cxt?.api('PUT', `/remittences/${remittence?.id}`, {
                params,
                success: () => {
                    cxt?.showMessage('S', t('Saved'));
                    setLoading(false);
                },
                error: (err: any) => {
                    cxt.showMessage('E', getErrorMessage(err));
                    setLoading(false);
                },
            });
        }
    };

    const delRemittence = (): void => {
        cxt?.api('DELETE', `/remittences/${remittence?.id}`, {
            confirmation: true,
            success: () => {
                navigate('/remittences');
                cxt?.showMessage('S', t('Deleted'));
            },
        });
    };

    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void => {
        if (name) {
            setRemittence({ ...remittence!, [name]: value });
        }
    };

    const downloadQ19 = (): void => {
        /* !!ATENCION!! Añadido parche para que en el centro de Puerto Real (3129) se envíe el parámetro fillBICs: true. */
        downloadFile(
            `/remittences/${remittence?.id}.q19`,
            'text/xml',
            fillBics ? { fillBICs: true } : undefined,
            'remesaSEPA.xml',
            'GET',
            null,
            (error) => {
                const errorMsg = getErrorMessage(error);
                cxt?.showMessage(
                    'E',
                    `${t('remittence.sepaDownloadError')}: ${errorMsg}`,
                );
            },
        );
    };

    const checkRemittence = (fix = false): void => {
        cxt?.api('PUT', `/remittences/${remittence?.id}/check`, {
            confirmation: fix,
            params: {
                fix,
            },
            success: () => {
                cxt?.showMessage('S', t('Saved'));
                loadRemittence();
                if (fix) checkRemittence();
            },
        });
    };

    const updateRemittenceLine = (
        remittenceLineUpdated: RemittenceLine,
    ): void => {
        setLines({
            ...lines!,
            rows: updateElementInArray(lines!.rows, remittenceLineUpdated),
        });
        setOpenRemittenceLine(null);
    };

    /**
     * Filter changes
     */
    useEffect(() => {
        loadLines();
    }, [filters.status]);

    const rejectSelectedLines = (selectedLines: RemittenceLine[]): void => {
        cxt?.api('POST', '/batch', {
            confirmation: true,
            params: {
                calls: selectedLines.map((line: any) => ({
                    method: 'PUT',
                    endpoint: `/remittences/lines/${line.id}/reject`,
                })),
            },
            success: () => {
                loadLines();
            },
        });
    };

    /**
     * Init
     */
    useEffect(() => {
        if (id) {
            if (id === 'create') {
                // Init data to create
                setRemittence({
                    id: 0,
                    name: '',
                    status: '',
                    issueDate: '',
                    created: '',
                });

                setCreating(true);
            } else {
                loadRemittence();
            }
        }
    }, [id]);

    return (
        <SttFullDialog
            open={!!remittence}
            onClose={() => navigate(getPath('remittences'))}
            title={remittence ? remittence.name : 'Report'}
        >
            <Box>
                <Paper>
                    <Box p={3}>
                        {remittence && (
                            <>
                                {!creating && (
                                    <>
                                        <Grid container spacing={3}>
                                            <SttLabelForm
                                                grid
                                                md={2}
                                                title={t('Created')}
                                                text={format(
                                                    parseISO(
                                                        remittence.created,
                                                    ),
                                                    'd-MM-yyyy, HH:mm',
                                                    { locale: getDateLocale() },
                                                )}
                                            />
                                        </Grid>

                                        <Box my={4}>
                                            <Divider />
                                        </Box>
                                    </>
                                )}

                                <Grid container spacing={3}>
                                    <SttFormText
                                        grid
                                        md={6}
                                        sm={12}
                                        xs={12}
                                        name="name"
                                        caption={t('Name')}
                                        defVal={remittence.name}
                                        onChange={handleForm}
                                    />

                                    <SttFormSelect
                                        grid
                                        md={3}
                                        sm={6}
                                        xs={6}
                                        name="status"
                                        caption={t('Status')}
                                        defVal={remittence.status}
                                        onChange={handleForm}
                                        options={
                                            cxt?.constants?.remittenceStatus
                                        }
                                    />

                                    <SttFormDate
                                        grid
                                        md={3}
                                        sm={6}
                                        xs={6}
                                        name="issueDate"
                                        caption={t('Issued')}
                                        defVal={remittence.issueDate}
                                        onChange={handleForm}
                                    />
                                    <Grid>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={fillBics}
                                                    onChange={() =>
                                                        setFillBics(!fillBics)
                                                    }
                                                />
                                            }
                                            label={t(
                                                'form.fillBicsOnSepaDownload',
                                            )}
                                        />
                                    </Grid>
                                </Grid>

                                <Box className="mt-6 flex justify-start gap-4 flex-wrap">
                                    {!creating && !remittence.trash && (
                                        <SttButtonDelete
                                            onClick={() => delRemittence()}
                                        />
                                    )}

                                    {!remittence.trash && (
                                        <SttButtonSave
                                            loading={loading}
                                            onClick={() => saveRemittence()}
                                        />
                                    )}

                                    {!creating && !remittence.trash && (
                                        <>
                                            <SttButton
                                                caption={`${t(
                                                    'generic.download',
                                                )} SEPA`}
                                                icon=<GetAppIcon />
                                                onClick={() => downloadQ19()}
                                            />
                                            {canRefund && (
                                                <SttButton
                                                    caption={t(
                                                        'RefundsRemittences',
                                                    )}
                                                    icon=<PublishIcon />
                                                    onClick={() =>
                                                        setOpenUploadFile(true)
                                                    }
                                                />
                                            )}
                                        </>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </Paper>

                {remittence && !remittence.trash && !creating && lines && (
                    <Box mt={3}>
                        <Paper>
                            <SttTopControls
                                p={2}
                                fields={[
                                    {
                                        caption: t('Name'),
                                        name: 'name',
                                        type: 'text',
                                        value: filters.name,
                                        onEnterKey: () => loadLines(),
                                        xs: 12,
                                        sm: 6,
                                        md: 4,
                                        lg: 3,
                                    },
                                ]}
                                onChange={({
                                    name,
                                    value,
                                }: {
                                    name: string | undefined;
                                    value: unknown;
                                }) => {
                                    if (!name) return;
                                    setFilters({
                                        ...filters,
                                        [name]: value as string,
                                    });
                                }}
                                onFilter={loadLines}
                                downloadButton={{
                                    onClick: () => downloadCSVLines(),
                                }}
                                menu={[
                                    {
                                        caption: t('Check'),
                                        icon: <CheckCircleOutlineIcon />,
                                        onClick: () => checkRemittence(false),
                                    },
                                    {
                                        caption: t('Fix'),
                                        icon: <BuildIcon />,
                                        onClick: () => checkRemittence(true),
                                    },
                                    {
                                        divider: true,
                                        caption: t('All'),
                                        icon: filters.status ? null : (
                                            <DoneIcon />
                                        ),
                                        onClick: () =>
                                            setFilters({
                                                ...filters,
                                                status: null,
                                            }),
                                    },
                                    {
                                        caption: t('sc.remittence.withErrors'),
                                        icon:
                                            filters.status === 'ERROR' ? (
                                                <DoneIcon />
                                            ) : null,
                                        onClick: () =>
                                            setFilters({
                                                ...filters,
                                                status: 'ERROR',
                                            }),
                                    },
                                    {
                                        caption: t('sc.remittence.onlyReturn'),
                                        icon:
                                            filters.status === 'RETURNED' ? (
                                                <DoneIcon />
                                            ) : null,
                                        onClick: () =>
                                            setFilters({
                                                ...filters,
                                                status: 'RETURNED',
                                            }),
                                    },
                                ]}
                            />

                            <SttTable
                                data={lines}
                                loading={false}
                                selectRows={selectRows}
                                setSelectRows={setSelectRows}
                                columns={[
                                    {
                                        title: t('Concept'),
                                        align: 'left' as const,
                                        value: (row: RemittenceLine) => (
                                            <Box>
                                                <Link
                                                    onClick={() =>
                                                        setOpenRemittenceLine(
                                                            row,
                                                        )
                                                    }
                                                >
                                                    {row.description}
                                                </Link>
                                                <Box>
                                                    <Typography variant="overline">
                                                        <Link
                                                            onClick={() => {
                                                                const mshipId =
                                                                    row.payment
                                                                        ?.user
                                                                        ?.mship
                                                                        ?.id;
                                                                if (
                                                                    mshipId !==
                                                                    undefined
                                                                ) {
                                                                    setMshipChosen(
                                                                        mshipId,
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            {row.name}
                                                        </Link>
                                                    </Typography>
                                                </Box>

                                                {row.errors &&
                                                    row.errors.map(
                                                        (error: any) => (
                                                            <Box
                                                                key={error}
                                                                mt={1}
                                                                style={{
                                                                    color: 'var(--destructive)',
                                                                }}
                                                            >
                                                                - {error}
                                                            </Box>
                                                        ),
                                                    )}
                                            </Box>
                                        ),
                                    },
                                    {
                                        type: 'dmy',
                                        title: t('Returned'),
                                        field: 'rejected',
                                        align: 'center' as const,
                                    },
                                    {
                                        type: 'counter',
                                        title: t('Errors'),
                                        align: 'center' as const,
                                        value: (row: RemittenceLine) =>
                                            row.errors && row.errors.length,
                                    },
                                    {
                                        type: 'price',
                                        title: t('Price'),
                                        field: 'price',
                                        align: 'right' as const,
                                        value: (row: RemittenceLine) => (
                                            <Link
                                                onClick={() =>
                                                    setIdPaymentModal(
                                                        row.payment.id,
                                                    )
                                                }
                                            >
                                                {formatPriceByLocale(
                                                    row.price,
                                                    row.priceShape?.currency,
                                                    row.priceShape?.locale,
                                                )}
                                            </Link>
                                        ),
                                    },
                                ]}
                                onFetch={loadLines}
                                selection={
                                    canRefund
                                        ? {
                                              actions: [
                                                  {
                                                      caption: t('Refund'),
                                                      onClick:
                                                          rejectSelectedLines,
                                                  },
                                              ],
                                          }
                                        : undefined
                                }
                            />
                        </Paper>
                    </Box>
                )}

                {idPaymentModal && (
                    <PaymentModal
                        idPayment={idPaymentModal}
                        onClose={() => setIdPaymentModal(null)}
                    />
                )}

                <RemittenceLineDialog
                    remittenceLine={openRemittenceLine}
                    onClose={() => setOpenRemittenceLine(null)}
                    onUpdate={(remittenceLine) =>
                        updateRemittenceLine(remittenceLine as RemittenceLine)
                    }
                />

                <UploadFileDialog
                    open={openUploadFile}
                    types={['REMITTENCE_REJECTIONS']}
                    onClose={() => setOpenUploadFile(false)}
                    uploadParams={{ idRemittence: remittence && remittence.id }}
                    onUploaded={(response: unknown) => {
                        const typedResponse = response as {
                            remittenceLines?: { rows: RemittenceLine[] };
                        };
                        if (
                            typedResponse &&
                            typedResponse.remittenceLines &&
                            typedResponse.remittenceLines.rows
                        ) {
                            setSelectRows(typedResponse.remittenceLines.rows);
                        }
                        setOpenUploadFile(false);
                        loadLines();
                    }}
                />

                <SttMshipDialog
                    open={mshipChosen !== null}
                    mshipId={mshipChosen}
                    onClose={() => {
                        setMshipChosen(null);
                        loadLines();
                    }}
                />
            </Box>
        </SttFullDialog>
    );
}
